import { InteractionStatus } from "@azure/msal-browser";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from "@azure/msal-react";
import {
  ChoiceGroup,
  IChoiceGroupOption,
  IComboBoxOption,
  IComboBoxStyles,
  IDropdownOption,
  IStackTokens,
  Icon,
  MessageBar,
  MessageBarType,
  ProgressIndicator,
  Separator,
  Shimmer,
  ShimmerElementType,
  Spinner,
  SpinnerSize,
  Stack,
  Toggle,
  TooltipHost,
  mergeStyleSets,
  mergeStyles,
  DirectionalHint,
  Callout,
  FocusTrapZone,
} from "@fluentui/react";
import { useBoolean, useId } from "@fluentui/react-hooks";
import { ActionButton, DefaultButton } from "@fluentui/react/lib/Button";
import { SearchBox } from "@fluentui/react/lib/SearchBox";
import { Text } from "@fluentui/react/lib/Text";
import { sort } from "fast-sort";
import React, { useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";
import { apiScopes } from "../authConfig";
import { ResourceGrid } from "../components/resources-grid";
import ResourceGridByProduct from "../components/resources-grid-by-prod";
import { errorsTypes } from "../errorTypes";
import { resource } from "../objects/resource";
import {
  getOwnedM365Groups,
  getOwnedStandaloneSPOSites,
} from "../services/resource-manager";
import {
  checkResourceCompliance,
  createAllResourcesList,
  loadFromLocalStorage,
} from "../utils";
import { ChangeType, SensitivityLabel } from "../enums";
import { toast } from "react-toastify";
import MainNavbar from "../components/navbar";
import { activityLog } from "../objects/activityLog";
import { department } from "../objects/department";
import { getGroupOwners } from "../services/graph";

// ── Types ─────────────────────────────────────────────────────────────────────
type LoadingPhase = "idle" | "partial" | "complete";

interface LoadingStatus {
  phase: LoadingPhase;
  message: string;
  subMessage: string;
  progressPercent: number;
}

type RefreshValue = string | department | undefined;

// Sort column definition
interface SortColumn {
  key: keyof resource | string;
  label: string;
  icon: string;
}

// ── Constants ─────────────────────────────────────────────────────────────────
const LOADING_STEPS: Record<string, LoadingStatus> = {
  idle: {
    phase: "idle",
    message: "Loading your workspaces…",
    subMessage:
      "We are retrieving your SharePoint sites. This may take a moment.",
    progressPercent: 10,
  },
  spoComplete: {
    phase: "partial",
    message: "SharePoint sites loaded!",
    subMessage: "Now loading your Teams and Viva Engage communities.",
    progressPercent: 40,
  },
  m365Complete: {
    phase: "partial",
    message: "Teams & M365 groups loaded!",
    subMessage: "Fetching workspace owners",
    progressPercent: 70,
  },
  ownersComplete: {
    phase: "complete",
    message: "All workspaces loaded!",
    subMessage: "",
    progressPercent: 100,
  },
};

const SORT_COLUMNS: SortColumn[] = [
  { key: "displayName", label: "Name", icon: "TextField" },
  { key: "resourceType", label: "Type", icon: "ViewAll" },
  { key: "complianceState", label: "Status", icon: "Shield" },
  { key: "renewDateFormatted", label: "Expiration", icon: "Calendar" },
  { key: "sensitivityLabel", label: "Sensitivity", icon: "ProtectedDocument" },
];

// ── Loading styles ────────────────────────────────────────────────────────────
const loadingStyles = mergeStyleSets({
  card: {
    background: "#ffffff",
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
    padding: "28px 32px",
    margin: "8px 0 20px",
    border: "1px solid #edebe9",
  },
  partialCard: {
    background: "#f0f6ff",
    borderRadius: "8px",
    border: "1px solid #c7e0f4",
    padding: "14px 20px",
    marginBottom: "16px",
  },
  progressContainer: {
    marginTop: "20px",
    marginBottom: "20px",
  },
  progressHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "8px",
  },
  stepsRow: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap" as const,
  },
  stepBadge: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 12px",
    borderRadius: "6px",
    flex: "1",
    minWidth: "160px",
    border: "1px solid #e1dfdd",
    background: "#f5f5f5",
    transition: "all 0.3s ease",
  },
  stepBadgeActive: {
    background: "#eff6fc",
    border: "1px solid #0078d4",
    boxShadow: "0 2px 4px rgba(0,120,212,0.2)",
  },
  stepBadgeDone: {
    background: "#dff6dd",
    border: "1px solid #107c10",
  },
  stepLabel: {
    fontSize: "13px",
    color: "#605e5c",
  },
  stepLabelActive: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#0078d4",
  },
  stepLabelDone: {
    fontSize: "13px",
    color: "#107c10",
  },
  shimmerWrapper: {
    opacity: 0.6,
    marginTop: "8px",
  },
  warningBox: {
    marginTop: "20px",
  },
});

// ── Page styles ───────────────────────────────────────────────────────────────
const pageStyles = mergeStyleSets({
  pageWrapper: {
    minHeight: "100vh",
    backgroundColor: "#f8f8f8",
  },
  contentArea: {
    padding: "0 24px 40px",
    maxWidth: "1700px",
    margin: "0 auto",
  },
  pageHeader: {
    padding: "28px 0 20px",
    borderBottom: "1px solid #edebe9",
    marginBottom: "24px",
  },
  pageTitle: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#201f1e",
    margin: 0,
    lineHeight: "32px",
  },
  pageSubtitle: {
    fontSize: "14px",
    color: "#605e5c",
    marginTop: "4px",
    marginBottom: 0,
  },

  // ── Sticky toolbar ──────────────────────────────────────────────────────
  // No negative margins — stays within contentArea's 1700px column,
  // so it naturally aligns with the table below it.
  stickyWrapper: {
    position: "sticky",
    top: 0,
    zIndex: 200,
    backdropFilter: "blur(6px)",
    WebkitBackdropFilter: "blur(6px)",
    background: "rgba(248,248,248,0.95)",
    boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
    // small negative horizontal margin + matching padding so the shadow
    // visually spans edge-to-edge within the content column, but content
    // still aligns with the table
    paddingLeft: "24px",
    paddingRight: "24px",
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "10px 0",
    flexWrap: "wrap" as const,
  },
  toolbarDivider: {
    width: "1px",
    height: "28px",
    background: "#edebe9",
    flexShrink: 0,
  },
  resultCount: {
    marginLeft: "auto",
    fontSize: "13px",
    color: "#605e5c",
    whiteSpace: "nowrap" as const,
  },

  // ── Filter + sort bar ───────────────────────────────────────────────────
  filterBar: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "6px 0 10px",
    flexWrap: "wrap" as const,
    borderTop: "1px solid #edebe9",
  },
  filterLabel: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#a19f9d",
    textTransform: "uppercase" as const,
    letterSpacing: "0.06em",
    marginRight: "4px",
    whiteSpace: "nowrap" as const,
  },

  // ── Sort chips ──────────────────────────────────────────────────────────
  sortBar: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "0 0 10px",
    flexWrap: "wrap" as const,
    borderTop: "1px solid #edebe9",
  },
  sortLabel: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#a19f9d",
    textTransform: "uppercase" as const,
    letterSpacing: "0.06em",
    marginRight: "4px",
    whiteSpace: "nowrap" as const,
  },
  sortChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    padding: "4px 10px",
    borderRadius: "14px",
    border: "1px solid #e1dfdd",
    background: "#ffffff",
    cursor: "pointer",
    fontSize: "12px",
    color: "#323130",
    transition: "all 0.15s ease",
    userSelect: "none" as const,
    selectors: {
      ":hover": {
        background: "#f3f2f1",
        borderColor: "#c8c6c4",
      },
    },
  },
  sortChipActive: {
    background: "#eff6fc",
    border: "1px solid #0078d4",
    color: "#0078d4",
    fontWeight: 600,
  },

  // ── Filter chips ────────────────────────────────────────────────────────
  filterChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    padding: "4px 10px",
    borderRadius: "14px",
    border: "1px solid #e1dfdd",
    background: "#ffffff",
    cursor: "pointer",
    fontSize: "12px",
    color: "#323130",
    transition: "all 0.15s ease",
    userSelect: "none" as const,
    selectors: {
      ":hover": {
        background: "#f3f2f1",
        borderColor: "#c8c6c4",
      },
    },
  },
  filterChipActive: {
    background: "#eff6fc",
    border: "1px solid #0078d4",
    color: "#0078d4",
    fontWeight: 600,
  },
  filterChipInactive: {
    background: "#faf9f8",
    border: "1px solid #e1dfdd",
    color: "#a19f9d",
    // no strikethrough
  },
});

// ── Step definitions ──────────────────────────────────────────────────────────
const STEP_DEFINITIONS = [
  {
    key: "spo",
    label: "SharePoint Sites",
    iconName: "SharepointLogo",
    doneAt: 40,
    activeFrom: 0,
    activeTo: 40,
  },
  {
    key: "m365",
    label: "Teams & Viva Engage",
    iconName: "TeamsLogo",
    doneAt: 70,
    activeFrom: 40,
    activeTo: 70,
  },
  {
    key: "owners",
    label: "Workspace Owners",
    iconName: "People",
    doneAt: 100,
    activeFrom: 70,
    activeTo: 100,
  },
];

// ── Step badges ───────────────────────────────────────────────────────────────
const renderStepBadges = (progressPercent: number): JSX.Element => (
  <div className={loadingStyles.stepsRow}>
    {STEP_DEFINITIONS.map((step) => {
      const isDone = progressPercent >= step.doneAt;
      const isActive =
        progressPercent >= step.activeFrom && progressPercent < step.activeTo;

      return (
        <div
          key={step.key}
          className={`${loadingStyles.stepBadge} ${
            isDone
              ? loadingStyles.stepBadgeDone
              : isActive
                ? loadingStyles.stepBadgeActive
                : ""
          }`}
        >
          <Icon
            iconName={step.iconName}
            styles={{
              root: {
                fontSize: 16,
                color: isDone ? "#107c10" : isActive ? "#0078d4" : "#a19f9d",
              },
            }}
          />
          <Text
            className={
              isDone
                ? loadingStyles.stepLabelDone
                : isActive
                  ? loadingStyles.stepLabelActive
                  : loadingStyles.stepLabel
            }
          >
            {step.label}
          </Text>
          {isDone && (
            <Icon
              iconName="CheckMark"
              styles={{
                root: {
                  marginLeft: "auto",
                  color: "#107c10",
                  fontWeight: 700,
                  fontSize: 14,
                },
              }}
              aria-label="Complete"
            />
          )}
          {isActive && !isDone && (
            <Spinner
              size={SpinnerSize.small}
              styles={{ root: { marginLeft: "auto" } }}
            />
          )}
        </div>
      );
    })}
  </div>
);

// ── Full loading card ─────────────────────────────────────────────────────────
interface LoadingCardFullProps {
  loadingStatus: LoadingStatus;
}
const LoadingCardFull: React.FC<LoadingCardFullProps> = ({ loadingStatus }) => (
  <div
    className={loadingStyles.card}
    role="status"
    aria-live="polite"
    aria-label={loadingStatus.message}
  >
    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 12 }}>
      <Spinner size={SpinnerSize.large} />
      <Stack tokens={{ childrenGap: 2 }}>
        <Text variant="large" styles={{ root: { fontWeight: 600 } }}>
          {loadingStatus.message}
        </Text>
        {loadingStatus.subMessage && (
          <Text variant="small" styles={{ root: { color: "#605e5c" } }}>
            {loadingStatus.subMessage}
          </Text>
        )}
      </Stack>
    </Stack>

    <div className={loadingStyles.progressContainer}>
      <div className={loadingStyles.progressHeader}>
        <Text variant="small" styles={{ root: { color: "#605e5c" } }}>
          Loading Progress
        </Text>
        <Text
          variant="small"
          styles={{ root: { fontWeight: 600, color: "#0078d4" } }}
        >
          {loadingStatus.progressPercent}%
        </Text>
      </div>
      <ProgressIndicator
        percentComplete={loadingStatus.progressPercent / 100}
        barHeight={8}
        styles={{
          root: { marginBottom: "4px" },
          progressBar: {
            borderRadius: "4px",
            background: "linear-gradient(90deg, #0078d4 0%, #106ebe 100%)",
          },
          progressTrack: { borderRadius: "4px" },
        }}
      />
    </div>

    {renderStepBadges(loadingStatus.progressPercent)}

    <div className={loadingStyles.shimmerWrapper}>
      {Array.from({ length: 6 }).map((_, index) => (
        <Shimmer
          key={index}
          shimmerElements={[
            { type: ShimmerElementType.circle, height: 28 },
            { type: ShimmerElementType.gap, width: "1%", height: 28 },
            { type: ShimmerElementType.line, height: 28 },
            { type: ShimmerElementType.gap, width: "5%", height: 28 },
          ]}
          styles={{ root: { marginBottom: "8px" } }}
        />
      ))}
    </div>

    <div className={loadingStyles.warningBox}>
      <MessageBar
        messageBarType={MessageBarType.warning}
        isMultiline
        styles={{ root: { borderRadius: "4px" } }}
      >
        <Text variant="small">
          <strong>Please do not refresh the page.</strong>
          <br />
          Loading your workspaces can take up to 2–3 minutes depending on how
          many you own. Your data is on its way!
        </Text>
      </MessageBar>
    </div>
  </div>
);

// ── Partial load banner ───────────────────────────────────────────────────────
interface PartialBannerProps {
  loadingStatus: LoadingStatus;
}
const PartialLoadBanner: React.FC<PartialBannerProps> = ({ loadingStatus }) => {
  if (loadingStatus.phase !== "partial") return null;

  return (
    <div
      className={loadingStyles.partialCard}
      role="status"
      aria-live="polite"
      aria-label={loadingStatus.message}
    >
      <Stack
        horizontal
        verticalAlign="center"
        tokens={{ childrenGap: 12 }}
        styles={{ root: { marginBottom: "12px" } }}
      >
        <Spinner size={SpinnerSize.medium} />
        <Stack tokens={{ childrenGap: 2 }} styles={{ root: { flex: 1 } }}>
          <Text variant="medium" styles={{ root: { fontWeight: 600 } }}>
            {loadingStatus.message}
          </Text>
          {loadingStatus.subMessage && (
            <Text variant="small" styles={{ root: { color: "#605e5c" } }}>
              {loadingStatus.subMessage}
            </Text>
          )}
        </Stack>
        <div style={{ minWidth: "180px", textAlign: "right" }}>
          <Text
            variant="medium"
            styles={{
              root: { fontWeight: 600, color: "#0078d4", marginBottom: "4px" },
            }}
          >
            {loadingStatus.progressPercent}%
          </Text>
          <ProgressIndicator
            percentComplete={loadingStatus.progressPercent / 100}
            barHeight={4}
            styles={{
              progressBar: { borderRadius: "4px" },
              progressTrack: { borderRadius: "4px" },
            }}
          />
        </div>
      </Stack>

      {renderStepBadges(loadingStatus.progressPercent)}
    </div>
  );
};

// ── Sort chip ─────────────────────────────────────────────────────────────────
interface SortChipProps {
  col: SortColumn;
  isActive: boolean;
  isAsc: boolean;
  onClick: (key: string) => void;
}
const SortChip: React.FC<SortChipProps> = ({
  col,
  isActive,
  isAsc,
  onClick,
}) => (
  <TooltipHost
    content={
      isActive
        ? `Sorted by ${col.label} (${isAsc ? "A → Z" : "Z → A"}) — click to reverse`
        : `Sort by ${col.label}`
    }
    directionalHint={DirectionalHint.bottomCenter}
  >
    <button
      type="button"
      role="button"
      aria-pressed={isActive}
      aria-label={`Sort by ${col.label}${isActive ? `, currently ${isAsc ? "ascending" : "descending"}` : ""}`}
      className={`${pageStyles.sortChip} ${isActive ? pageStyles.sortChipActive : ""}`}
      onClick={() => onClick(col.key as string)}
    >
      <Icon
        iconName={col.icon}
        styles={{ root: { fontSize: 12 } }}
        aria-hidden
      />
      {col.label}
      {isActive && (
        <Icon
          iconName={isAsc ? "SortUp" : "SortDown"}
          styles={{ root: { fontSize: 11, marginLeft: 2 } }}
          aria-hidden
        />
      )}
    </button>
  </TooltipHost>
);

// ── Filter chip ───────────────────────────────────────────────────────────────
interface FilterChipProps {
  label: string;
  icon: string;
  active: boolean;
  onToggle: () => void;
  tooltip: string;
}
const FilterChip: React.FC<FilterChipProps> = ({
  label,
  icon,
  active,
  onToggle,
  tooltip,
}) => (
  <TooltipHost content={tooltip} directionalHint={DirectionalHint.bottomCenter}>
    <button
      type="button"
      role="switch"
      aria-checked={active}
      aria-label={tooltip}
      className={`${pageStyles.filterChip} ${
        active ? pageStyles.filterChipActive : pageStyles.filterChipInactive
      }`}
      onClick={onToggle}
    >
      <Icon
        iconName={active ? "CheckboxComposite" : "Checkbox"}
        styles={{
          root: {
            fontSize: 12,
            color: active ? "#0078d4" : "#a19f9d",
          },
        }}
        aria-hidden
      />
      <Icon
        iconName={icon}
        styles={{
          root: {
            fontSize: 12,
            color: active ? "#0078d4" : "#a19f9d",
          },
        }}
        aria-hidden
      />
      {label}
    </button>
  </TooltipHost>
);

// ── Main component ────────────────────────────────────────────────────────────
export const ResourceList: React.FunctionComponent = () => {
  // ── State ──────────────────────────────────────────────────────────────────
  const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>(
    LOADING_STEPS.idle,
  );
  const [showAllSpaces, setShowAllSpaces] = useState<boolean>(false);
  const [useAscendantSort, setUseAscendantSort] = useState<boolean>(true);
  const [showTeams, setShowTeams] = useState<boolean>(true);
  const [showSPO, setShowSPO] = useState<boolean>(true);
  const [showYammer, setShowYammer] = useState<boolean>(true);
  const [showCompliance, setShowCompliance] = useState<boolean>(true);
  const [resourceLoadComplete, setResourceLoadComplete] =
    useState<boolean>(false);
  const [selectedSortColumn, setSelectedSortColumn] =
    useState<string>("displayName");
  const [searchTerm, setSearchTerm] = useState<string>("");

  const { instance, inProgress, accounts } = useMsal();

  const hasFetched = React.useRef<boolean>(false);
  const loadingIsComplete = React.useRef<boolean>(false);

  const [baseResourceList, setBaseResourceList] =
    useState<Array<resource> | null>(null);
  const [currentResourceList, setCurrentResourceList] = useState<
    Array<resource>
  >([]);

  // ── Sort chip handler ──────────────────────────────────────────────────────
  // Clicking the active column reverses direction; clicking a new column
  // sets it as active and resets to ascending.
  const handleSortChipClick = (key: string): void => {
    if (key === selectedSortColumn) {
      // toggle direction
      setUseAscendantSort((prev) => !prev);
    } else {
      setSelectedSortColumn(key);
      setUseAscendantSort(true);
    }
  };

  // Re-apply filters whenever sort or filter state changes
  useEffect(() => {
    applySelectedFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedSortColumn,
    useAscendantSort,
    showTeams,
    showSPO,
    showYammer,
    showCompliance,
  ]);

  // ── Deep-link handler ─────────────────────────────────────────────────────
  useEffect(() => {
    if (loadingStatus.phase !== "complete") return;

    const searchParams = new URLSearchParams(window.location.search);
    if (!searchParams.has("resourceid") || !searchParams.has("isconnected"))
      return;

    const resourceId = searchParams.get("resourceid");
    const isConnected = searchParams.get("isconnected");

    const attemptResourceSelection = (
      attempt: number = 1,
      maxAttempts: number = 10,
    ): void => {
      if (loadingIsComplete.current) return;

      const elements = document.querySelectorAll<HTMLButtonElement>(
        `[data-resourceid="${resourceId}"][data-isconnected="${isConnected}"]`,
      );

      if (elements.length > 0) {
        elements.forEach((element) => {
          if (element.offsetParent !== null && !element.disabled) {
            element.scrollIntoView({
              behavior: "smooth",
              block: "center",
              inline: "nearest",
            });
            setTimeout(() => {
              if (element.offsetParent !== null && !element.disabled) {
                element.focus();
                element.click();
                const clickEvent = new MouseEvent("click", {
                  bubbles: true,
                  cancelable: true,
                  view: window,
                });
                element.dispatchEvent(clickEvent);
                loadingIsComplete.current = true;
              }
            }, 500);
          }
        });
      } else if (attempt < maxAttempts) {
        setTimeout(
          () => attemptResourceSelection(attempt + 1, maxAttempts),
          200 * attempt,
        );
      } else {
        console.warn(
          `Failed to find resource after ${maxAttempts} attempts:`,
          resourceId,
        );
        loadingIsComplete.current = true;
      }
    };

    attemptResourceSelection();
  }, [loadingStatus.phase]);

  // ── Search helpers ─────────────────────────────────────────────────────────
  function clearSearch(): void {
    setSearchTerm("");
    setCurrentResourceList([...(baseResourceList as Array<resource>)]);
  }

  function applySelectedFilters(searchCriteria: string = ""): void {
    if (baseResourceList !== null) {
      let filterResourceList: Array<resource> = [...baseResourceList];

      if (searchCriteria !== "") {
        setSearchTerm(searchCriteria);
        filterResourceList = filterResourceList.filter((element) =>
          element.displayName
            .toLowerCase()
            .includes(searchCriteria.toLowerCase()),
        );
      } else if (searchTerm !== "") {
        filterResourceList = filterResourceList.filter((element) =>
          element.displayName.toLowerCase().includes(searchTerm.toLowerCase()),
        );
      }

      if (!showTeams) {
        filterResourceList = filterResourceList.filter(
          (element) => element.resourceType !== "Teams",
        );
      }
      if (!showSPO) {
        filterResourceList = filterResourceList.filter(
          (element) =>
            element.resourceType !== "SPO" && element.resourceType !== "CSPO",
        );
      }
      if (!showYammer) {
        filterResourceList = filterResourceList.filter(
          (element) => element.resourceType !== "Yammer",
        );
      }
      if (!showCompliance) {
        filterResourceList = filterResourceList.filter(
          (element) => element.isCompliant === false,
        );
      }

      const sortDir = useAscendantSort ? "asc" : "desc";
      const sortKey = selectedSortColumn as keyof resource;
      filterResourceList = sort(filterResourceList)[sortDir](
        (element) => element[sortKey],
      );

      setCurrentResourceList(filterResourceList);
    }
  }

  // ── Dataset refresh ────────────────────────────────────────────────────────
  const refreshMainDataset = (
    resourceID: string = "",
    changeType: ChangeType,
    value: RefreshValue,
  ): void => {
    setResourceLoadComplete(false);
    let filterResourceList: Array<resource> = [
      ...(baseResourceList as resource[]),
    ];

    filterResourceList.forEach((element) => {
      if (
        element.azureAdId === resourceID ||
        element.siteUrl === resourceID ||
        element.siteId === resourceID
      ) {
        if (changeType === ChangeType.UpdateLease) {
          const tempDate = new Date();
          tempDate.setMonth(tempDate.getMonth() + 6);
          element.renewDateFormatted = tempDate;
        }
        if (changeType === ChangeType.UpdateLeaseInternal) {
          const tempDate = new Date();
          tempDate.setMonth(tempDate.getMonth() + 6);
          element.renewDateFormatted = tempDate;
          element.sensitivityLabel = SensitivityLabel.InternalOnly;
        }
        if (changeType === ChangeType.AddOwner) {
          element.owners.push(value as string);
        }
        if (changeType === ChangeType.AddNonValidOwner) {
          element.nonValidOwners.push(value as string);
        }
        if (changeType === ChangeType.RemoveOwner) {
          element.owners = element.owners.filter(
            (owner) => owner !== (value as string),
          );
        }
        if (changeType === ChangeType.RemoveNonValidOwner) {
          element.nonValidOwners = element.nonValidOwners.filter(
            (owner) => owner !== (value as string),
          );
        }
        if (changeType === ChangeType.UpdateDepartment) {
          const newDepartment = value as department;
          element.departmentCd = newDepartment.departmentCd!;
          element.departmentNm = newDepartment.departmentNm!;
        }

        element.compliance = checkResourceCompliance(element);
        element.isCompliant = element.compliance.every(
          (compResult) => compResult.isCompliant === true,
        );
      }
    });

    if (changeType === ChangeType.DeleteGroup) {
      filterResourceList = filterResourceList.filter(
        (o) => o.azureAdId !== resourceID,
      );
    }
    if (changeType === ChangeType.DeleteSite) {
      filterResourceList = filterResourceList.filter(
        (o) => o.siteUrl !== resourceID,
      );
    }

    setBaseResourceList(filterResourceList);
    setCurrentResourceList(filterResourceList);
    setResourceLoadComplete(true);
  };

  // ── Main data fetch ────────────────────────────────────────────────────────
  useEffect(() => {
    if (hasFetched.current || inProgress !== InteractionStatus.None) return;

    hasFetched.current = true;

    async function fetchData() {
      try {
        setLoadingStatus(LOADING_STEPS.idle);

        const RMTAccessTokenRequest = {
          scopes: apiScopes.resourceManagementScopes,
          account: accounts[0],
        };
        const RMTAccessTokenResponse = await instance.acquireTokenSilent(
          RMTAccessTokenRequest,
        );

        const spoGroupsResult = await getOwnedStandaloneSPOSites(
          RMTAccessTokenResponse.accessToken,
        );

        let partialResourceList = createAllResourcesList(spoGroupsResult, []);
        partialResourceList = sort(partialResourceList).asc(
          (element) => element.displayName,
        );
        partialResourceList.forEach((element) => {
          element.compliance = checkResourceCompliance(element);
          element.isCompliant = element.compliance.every(
            (compResult) => compResult.isCompliant === true,
          );
        });

        setBaseResourceList(partialResourceList);
        setCurrentResourceList(partialResourceList);
        setLoadingStatus(LOADING_STEPS.spoComplete);
        setResourceLoadComplete(true);

        const m365GroupsResult = await getOwnedM365Groups(
          RMTAccessTokenResponse.accessToken,
        );

        let unifiedResourceList = createAllResourcesList(
          spoGroupsResult,
          m365GroupsResult,
        );
        unifiedResourceList = sort(unifiedResourceList).asc(
          (element) => element.renewDateFormatted,
        );
        unifiedResourceList.forEach((element) => {
          element.compliance = checkResourceCompliance(element);
          element.isCompliant = element.compliance.every(
            (compResult) => compResult.isCompliant === true,
          );
        });

        setBaseResourceList(unifiedResourceList);
        setCurrentResourceList(unifiedResourceList);
        setLoadingStatus(LOADING_STEPS.m365Complete);

        try {
          const graphAccessTokenRequest = {
            scopes: apiScopes.graphScopes,
            account: accounts[0],
          };
          const graphTokenResponse = await instance.acquireTokenSilent(
            graphAccessTokenRequest,
          );

          const ownerTaskList = m365GroupsResult.map(async (group) => {
            try {
              group.owners = await getGroupOwners(
                graphTokenResponse.accessToken,
                group.governanceEntry?.id as string,
              );
            } catch {
              group.owners = [];
            }
          });

          await Promise.all(ownerTaskList);

          let enrichedList = createAllResourcesList(
            spoGroupsResult,
            m365GroupsResult,
          );
          enrichedList = sort(enrichedList).asc(
            (element) => element.renewDateFormatted,
          );
          enrichedList.forEach((element) => {
            element.compliance = checkResourceCompliance(element);
            element.isCompliant = element.compliance.every(
              (compResult) => compResult.isCompliant === true,
            );
          });

          setBaseResourceList(enrichedList);
          setCurrentResourceList(enrichedList);
          setLoadingStatus(LOADING_STEPS.ownersComplete);
          loadingIsComplete.current = true;
          toast.dismiss();
        } catch {
          setLoadingStatus(LOADING_STEPS.ownersComplete);
          loadingIsComplete.current = true;
          toast.warning(
            "Workspaces loaded, but some owner data could not be retrieved.",
            { autoClose: 4000 },
          );
        }
      } catch (ex) {
        if (!loadingIsComplete.current) {
          setResourceLoadComplete(true);
          setLoadingStatus(LOADING_STEPS.ownersComplete);
          loadingIsComplete.current = true;
          Swal.fire(
            errorsTypes.somethingWentWrong.errorTitle,
            errorsTypes.somethingWentWrong.errorSubtitle,
            "error",
          );
        }
      }
    }

    fetchData();
  }, [accounts, inProgress, instance]);

  // ── Derived values ─────────────────────────────────────────────────────────
  const resultCountLabel =
    currentResourceList.length === 1
      ? "1 workspace"
      : `${currentResourceList.length} workspaces`;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className={pageStyles.pageWrapper}>
      <MainNavbar
        userName={window.CURRENT_USER_EMAIL}
        userActivity={loadFromLocalStorage<activityLog>("userActivityData")}
      />

      <div className={pageStyles.contentArea}>
        <div className={pageStyles.pageHeader}>
          <h1 className={pageStyles.pageTitle}>My Workspaces</h1>
          <p className={pageStyles.pageSubtitle}>
            Manage and review all workspaces you own across SharePoint, Teams,
            and Viva Engage.
          </p>
        </div>

        <AuthenticatedTemplate>
          {/* ── Sticky toolbar + filter/sort bars ─────────────────────── */}
          <div className={pageStyles.stickyWrapper}>
            {/* Row 1 — search + group toggle + count */}
            <div className={pageStyles.toolbar}>
              <TooltipHost content="Type a workspace name and press Enter to search">
                <SearchBox
                  placeholder="Search workspaces…"
                  onClear={clearSearch}
                  value={searchTerm}
                  styles={{
                    root: { width: 300, maxWidth: "100%", border: "none" },
                  }}
                  onSearch={(newValue: string) => {
                    if (newValue && newValue.trim().length >= 2) {
                      applySelectedFilters(newValue.trim());
                    } else if (newValue === "") {
                      clearSearch();
                    }
                  }}
                  onChange={(_, newValue) => {
                    if (newValue === "" || newValue === undefined) {
                      clearSearch(); // box cleared → reset list
                    } else {
                      setSearchTerm(newValue); // ✅ keep controlled input in sync
                      applySelectedFilters(newValue); // ✅ live filter as user types
                    }
                  }}
                />
              </TooltipHost>

              <div className={pageStyles.toolbarDivider} aria-hidden="true" />

              <TooltipHost content="Switch between a flat list and a view grouped by product">
                <Toggle
                  label="Group by product"
                  onText="On"
                  offText="Off"
                  checked={showAllSpaces}
                  onChange={(_, checked) =>
                    setShowAllSpaces(checked as boolean)
                  }
                  inlineLabel
                  styles={{
                    root: { margin: 0 },
                    label: { fontSize: "13px", color: "#605e5c" },
                  }}
                />
              </TooltipHost>

              {resourceLoadComplete && loadingStatus.phase === "complete" && (
                <Text className={pageStyles.resultCount}>
                  {resultCountLabel}
                </Text>
              )}
            </div>

            {/* Row 2 — filter chips (only when data is loaded) */}
            {resourceLoadComplete && (
              <div
                className={pageStyles.filterBar}
                role="group"
                aria-label="Workspace type filters"
              >
                <span className={pageStyles.filterLabel}>Show:</span>

                <FilterChip
                  label="Teams"
                  icon="TeamsLogo"
                  active={showTeams}
                  onToggle={() => setShowTeams((v) => !v)}
                  tooltip={
                    showTeams
                      ? "Hide Teams workspaces"
                      : "Show Teams workspaces"
                  }
                />
                <FilterChip
                  label="SharePoint"
                  icon="SharepointLogo"
                  active={showSPO}
                  onToggle={() => setShowSPO((v) => !v)}
                  tooltip={
                    showSPO ? "Hide SharePoint sites" : "Show SharePoint sites"
                  }
                />
                <FilterChip
                  label="Viva Engage"
                  icon="YammerLogo"
                  active={showYammer}
                  onToggle={() => setShowYammer((v) => !v)}
                  tooltip={
                    showYammer
                      ? "Hide Viva Engage communities"
                      : "Show Viva Engage communities"
                  }
                />
                <FilterChip
                  label="Non-compliant only"
                  icon="Shield"
                  active={!showCompliance}
                  onToggle={() => setShowCompliance((v) => !v)}
                  tooltip={
                    showCompliance
                      ? "Click to show only non-compliant workspaces"
                      : "Click to show all workspaces"
                  }
                />

                <div className={pageStyles.toolbarDivider} aria-hidden="true" />

                {/* Row 2b — sort chips inline with filters */}
                <span className={pageStyles.sortLabel}>Sort:</span>
                {SORT_COLUMNS.map((col) => (
                  <SortChip
                    key={col.key as string}
                    col={col}
                    isActive={selectedSortColumn === col.key}
                    isAsc={useAscendantSort}
                    onClick={handleSortChipClick}
                  />
                ))}
              </div>
            )}
          </div>
          {/* ── End sticky wrapper ─────────────────────────────────────── */}

          {/* Full loading card */}
          {!resourceLoadComplete && (
            <LoadingCardFull loadingStatus={loadingStatus} />
          )}

          {/* Grid + optional partial banner */}
          {resourceLoadComplete && (
            <>
              <PartialLoadBanner loadingStatus={loadingStatus} />
              <br></br>
              {!showAllSpaces ? (
                <ResourceGrid
                  ownedResources={currentResourceList}
                  resourceType="Any"
                  refreshDatasetFunction={refreshMainDataset}
                />
              ) : (
                <ResourceGridByProduct
                  ownedResources={currentResourceList}
                  refreshDatasetFunction={refreshMainDataset}
                />
              )}
            </>
          )}
        </AuthenticatedTemplate>

        <UnauthenticatedTemplate>
          <MessageBar
            messageBarType={MessageBarType.warning}
            styles={{ root: { borderRadius: "6px", marginTop: "24px" } }}
          >
            You are not signed in. Please sign in to view your workspaces.
          </MessageBar>
        </UnauthenticatedTemplate>
      </div>
    </div>
  );
};

export default ResourceList;
